import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, Calendar } from "lucide-react";
import Navbar from "@/components/Navbar";
import { fetchShare, fetchConfig } from "@/lib/api";

const BAND_COLOR = {
  "High Friction": { bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200", bar: "#EF4444" },
  "Emerging Readiness": { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", bar: "#F59E0B" },
  "Operationally Ready": { bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200", bar: "#2563EB" },
  "Transformation Ready": { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", bar: "#10B981" },
};

const Share = () => {
  const { id } = useParams();
  const shareQuery = useQuery({ queryKey: ["share", id], queryFn: () => fetchShare(id), retry: false });
  const configQuery = useQuery({ queryKey: ["config"], queryFn: fetchConfig, retry: false });

  if (shareQuery.isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-32 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      </div>
    );
  }

  if (shareQuery.error || !shareQuery.data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-bold text-slate-900">Snapshot not found</h1>
          <p className="mt-3 text-slate-600">This link may have expired or been mistyped.</p>
          <div className="mt-6">
            <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
              <Link to="/">Take the assessment</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const data = shareQuery.data;
  const band = BAND_COLOR[data.readiness_band] || BAND_COLOR["Operationally Ready"];
  const calendlyUrl = configQuery.data?.calendly_url || "https://calendly.com/paul-therivercompany/30min";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10 lg:py-16">
        {/* Header */}
        <div className="mb-8" data-testid="share-header">
          <div className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500 mb-2">Enterprise Alignment Readiness · Public Snapshot</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Readiness snapshot</h1>
          <p className="mt-3 text-lg text-slate-600">An anonymous view of one organization&apos;s scored readiness across six dimensions.</p>
        </div>

        {/* Score card */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl p-8">
            <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Overall Score</div>
            <div className="flex items-baseline gap-2">
              <span data-testid="share-overall-score" className="font-display text-6xl font-bold text-slate-900 tracking-tighter">{data.overall_score}</span>
              <span className="text-slate-400 text-xl">/100</span>
            </div>
            <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${band.bg} ${band.text} ring-1 ${band.ring} text-sm font-semibold`} data-testid="share-band">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: band.bar }} />
              {data.readiness_band}
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-semibold tracking-widest uppercase text-emerald-700 mb-1">Strongest</div>
                <div className="text-sm font-medium text-slate-900 leading-tight">{data.highest_category}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold tracking-widest uppercase text-amber-700 mb-1">Focus area</div>
                <div className="text-sm font-medium text-slate-900 leading-tight">{data.lowest_category}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-[#312D44] p-8 text-white">
            <div className="absolute inset-0 opacity-[0.06]" style={{
              backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
              backgroundSize: "32px 32px"
            }} />
            <div className="relative">
              <div className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Recommended Pathway</div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-3">{data.recommended_service}</h2>
              <p className="text-slate-300 leading-relaxed">{data.service_description}</p>
            </div>
          </div>
        </div>

        {/* Category breakdown */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8">
          <div className="flex items-baseline justify-between mb-6">
            <h3 className="font-display text-xl font-semibold text-slate-900">Category Breakdown</h3>
            <div className="text-xs text-slate-500 font-mono">Scores out of 5</div>
          </div>
          <div className="space-y-4">
            {data.category_scores.map((c) => {
              const isLowest = c.category === data.lowest_category;
              const isHighest = c.category === data.highest_category;
              return (
                <div key={c.category}>
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{c.category}</span>
                      {isHighest && <span className="text-[10px] font-semibold tracking-wider uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Strongest</span>}
                      {isLowest && <span className="text-[10px] font-semibold tracking-wider uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Focus Area</span>}
                    </div>
                    <span className="font-mono text-sm font-semibold text-slate-900">{c.score.toFixed(2)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(c.score / 5) * 100}%`, background: isLowest ? "#F59E0B" : isHighest ? "#10B981" : "#2563EB" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Conversion CTA */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#312D44] to-[#1F1B30] p-8 sm:p-12 text-white">
          <div className="absolute inset-0 opacity-[0.08]" style={{
            backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "32px 32px"
          }} />
          <div className="relative">
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white max-w-2xl">Where does your organization stand?</h3>
            <p className="mt-3 text-blue-100 max-w-xl leading-relaxed">Take the 12-question Enterprise Alignment Readiness Assessment and get your own scored result and recommended pathway in under twelve minutes.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild data-testid="share-take-assessment-btn" className="bg-white text-slate-900 hover:bg-slate-100 rounded-md px-5 py-5 h-auto font-medium">
                <Link to="/assessment">Take the assessment <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-md px-5 py-5 h-auto font-medium">
                <a href={calendlyUrl} target="_blank" rel="noopener noreferrer"><Calendar className="w-4 h-4 mr-2" /> Schedule a 30-minute Enterprise Alignment Review</a>
              </Button>
            </div>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-slate-400">
          This is a public, anonymized snapshot. No personal or organizational identifiers are shown.
        </p>
      </div>
    </div>
  );
};

export default Share;
